import React, {FC, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Alert, Button, Card, Space, Spin, Typography, App} from "antd";
import {SaveOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {AuthContext} from "../../Contexts/AuthContext";
import {FileUploadUrlInput} from "../../Components/FileUploadUrlInput";
import {useSelectedEventWorkspace} from "./EventWorkspaceLayout";
import TemplatePlacementEditor, {A6_TEMPLATE_CANVAS} from "./TemplatePlacementEditor";
import TemplateTextFieldList from "./TemplateTextFieldList";
import {
    createDefaultCertificateTextFields,
    serializeTemplateTextFields,
    TemplateTextFieldDefinition,
    toTemplatePlacement
} from "./templateTextFields";
import {
    buildEventTemplateSettingsSaveInput,
    getEventTemplateBranding
} from "./eventTemplateEditorData";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../../userFacingErrorUtils";
import {downloadCertificateTemplatePreview} from "./templatePreviewDownload";
import {StatusTag} from "../../Components/ui/StatusTag";
import {PageHeader} from "../../Components/ui";
import {
    useEventTemplateSettingsQuery,
    useSaveEventTemplateSettingsMutation
} from "../../GraphQL/generated/graphql";

const CERTIFICATE_CANVAS = {
    width: A6_TEMPLATE_CANVAS.width,
    height: A6_TEMPLATE_CANVAS.height
};

const CERTIFICATE_DEFAULT_NAME_POSITION = {
    x: A6_TEMPLATE_CANVAS.width / 2,
    y: A6_TEMPLATE_CANVAS.height / 2
};

type CertificateTemplateState = {
    certificateTemplateUrl: string;
    certificateParticipantNameX: number;
    certificateParticipantNameY: number;
    certificateTextFields: TemplateTextFieldDefinition[];
    certificateTextFieldsJson: string;
};

const textValue = (value?: string | null) => value?.trim() ?? "";

const buildCertificateState = (
    branding: ReturnType<typeof getEventTemplateBranding>
): CertificateTemplateState => {
    const state = {
        certificateTemplateUrl: textValue(branding?.certificateTemplateUrl),
        certificateParticipantNameX: branding?.certificateParticipantNameX ?? CERTIFICATE_DEFAULT_NAME_POSITION.x,
        certificateParticipantNameY: branding?.certificateParticipantNameY ?? CERTIFICATE_DEFAULT_NAME_POSITION.y,
        certificateTextFieldsJson: textValue(branding?.certificateTextFieldsJson)
    };

    const certificateTextFields = createDefaultCertificateTextFields({
        ...state,
        certificateTextFieldsJson: state.certificateTextFieldsJson
    });

    return {
        ...state,
        certificateTextFields
    };
};

const syncLegacyCertificateCoordinates = (state: CertificateTemplateState): CertificateTemplateState => {
    const participantName = state.certificateTextFields.find(field => field.id === "participantName")
        ?? state.certificateTextFields.find(field => field.source === "participant" && field.participantField === "fullName")
        ?? state.certificateTextFields[0];

    return {
        ...state,
        certificateParticipantNameX: participantName?.x ?? state.certificateParticipantNameX,
        certificateParticipantNameY: participantName?.y ?? state.certificateParticipantNameY,
        certificateTextFieldsJson: serializeTemplateTextFields(state.certificateTextFields)
    };
};

export const EventCertificateTemplatePage: FC = () => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const {auth} = useContext(AuthContext);
    const {eventId} = useSelectedEventWorkspace();
    const errorLabels = buildUserFacingErrorLabels(t);
    const [state, setState] = useState<CertificateTemplateState>(() => buildCertificateState(null));
    const [downloadingPreview, setDownloadingPreview] = useState(false);
    const query = useEventTemplateSettingsQuery({
        variables: {id: eventId},
        fetchPolicy: "cache-and-network"
    });
    const [saveTemplateSettings, {loading: saving}] = useSaveEventTemplateSettingsMutation();
    const eventBasics = query.data?.eventById;
    const branding = useMemo(() => getEventTemplateBranding(query.data), [query.data]);
    const isReadyForGeneration = !!textValue(state.certificateTemplateUrl)
        && state.certificateParticipantNameX >= 0
        && state.certificateParticipantNameY >= 0;

    const fileUploadLabels = useMemo(() => ({
        upload: t("upload-file", {defaultValue: "Upload file"}),
        clear: t("clear", {defaultValue: "Clear"}),
        open: t("open", {defaultValue: "Open"}),
        empty: t("file-not-selected", {defaultValue: "No file selected"}),
        success: t("file-uploaded", {defaultValue: "File uploaded"}),
        error: t("error.upload-failed", {defaultValue: "Upload failed"})
    }), [t]);

    useEffect(() => {
        if (!query.loading) {
            setState(buildCertificateState(branding));
        }
    }, [branding, query.loading]);

    const reload = useCallback(async () => {
        const result = await query.refetch();
        setState(buildCertificateState(getEventTemplateBranding(result.data)));
    }, [query]);

    const save = async () => {
        if (!eventBasics) {
            message.error(t("template-event-data-unavailable", {
                defaultValue: "Event data is not loaded yet, so the template cannot be saved. Reload the page and try again."
            }));
            return;
        }

        try {
            await saveTemplateSettings({
                variables: {
                    input: buildEventTemplateSettingsSaveInput(eventBasics, branding, syncLegacyCertificateCoordinates(state))
                }
            });
            message.success(t("certificate-template.saved", {defaultValue: "Certificate template saved"}));
            await reload();
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    };

    const updateTextField = (id: string, changes: Partial<TemplateTextFieldDefinition>) => {
        setState(previous => ({
            ...previous,
            certificateTextFields: previous.certificateTextFields.map(field => field.id === id ? {...field, ...changes} : field)
        }));
    };

    const addTextField = () => {
        setState(previous => ({
            ...previous,
            certificateTextFields: [
                ...previous.certificateTextFields,
                {
                    id: `certificate-field-${Date.now()}`,
                    label: t("template-field", {defaultValue: "Field"}),
                    source: "static",
                    participantField: "",
                    staticText: t("template-field-static-sample", {defaultValue: "Text"}),
                    side: "front",
                    x: CERTIFICATE_DEFAULT_NAME_POSITION.x,
                    y: CERTIFICATE_DEFAULT_NAME_POSITION.y - 120
                }
            ]
        }));
    };

    const removeTextField = (id: string) => {
        setState(previous => ({
            ...previous,
            certificateTextFields: previous.certificateTextFields.filter(field => field.id !== id)
        }));
    };

    const downloadPreview = async () => {
        setDownloadingPreview(true);
        try {
            await downloadCertificateTemplatePreview(eventId, {
                templateUrl: state.certificateTemplateUrl,
                textFields: state.certificateTextFields
            }, auth.token);
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        } finally {
            setDownloadingPreview(false);
        }
    };

    const fieldListLabels = {
        add: t("template-field-add", {defaultValue: "Add field"}),
        expand: t("template-field-expand", {defaultValue: "Edit parameters"}),
        collapse: t("template-field-collapse", {defaultValue: "Hide parameters"}),
        backgroundColor: t("template-field-background-color", {defaultValue: "Background"}),
        backgroundOpacity: t("template-field-background-opacity", {defaultValue: "Background opacity"}),
        borderColor: t("template-field-border-color", {defaultValue: "Border"}),
        borderOpacity: t("template-field-border-opacity", {defaultValue: "Border opacity"}),
        borderRadius: t("template-field-border-radius", {defaultValue: "Radius"}),
        borderWidth: t("template-field-border-width", {defaultValue: "Border width"}),
        color: t("template-field-color", {defaultValue: "Text color"}),
        fieldLabel: t("template-field-label", {defaultValue: "Label"}),
        fontFamily: t("template-field-font-family", {defaultValue: "Font"}),
        fontSize: t("template-field-font-size", {defaultValue: "Font size"}),
        fontStyle: t("template-field-font-style", {defaultValue: "Style"}),
        fontStyleItalic: t("template-field-font-style-italic", {defaultValue: "Italic"}),
        fontStyleNormal: t("template-field-font-style-normal", {defaultValue: "Normal"}),
        fontWeight: t("template-field-font-weight", {defaultValue: "Weight"}),
        participantField: t("template-field-participant-field", {defaultValue: "Participant field"}),
        positionX: t("template-field-position-x", {defaultValue: "X position"}),
        positionY: t("template-field-position-y", {defaultValue: "Y position"}),
        remove: t("template-field-remove", {defaultValue: "Remove field"}),
        shadow: t("template-field-shadow", {defaultValue: "Shadow"}),
        source: t("template-field-source", {defaultValue: "Source"}),
        sourceParticipant: t("template-field-source-participant", {defaultValue: "Participant"}),
        sourceStatic: t("template-field-source-static", {defaultValue: "Static text"}),
        staticText: t("template-field-static-text", {defaultValue: "Static text"}),
        title: t("template-fields", {defaultValue: "Fields"}),
        type: t("template-field-type", {defaultValue: "Type"}),
        typeQr: t("template-field-type-qr", {defaultValue: "QR code"}),
        typeText: t("template-field-type-text", {defaultValue: "Text"}),
        size: t("template-field-size", {defaultValue: "Size"})
    };
    const previewAlt = t("certificate-template.preview-alt", {defaultValue: "Certificate template preview"});

    if (query.loading && !eventBasics) {
        return <Spin/>;
    }

    if (query.error) {
        return (
            <Alert
                type="error"
                message={t("certificate-template.load-error", {defaultValue: "Certificate template unavailable"})}
                description={getUserFacingErrorMessage(query.error, t, errorLabels)}
            />
        );
    }

    return (
        <div className="ati-stack">
            <PageHeader
                level={2}
                title={t("certificate-template.title", {defaultValue: "Certificate template"})}
                actions={(
                    <StatusTag tone={isReadyForGeneration ? "success" : "warning"}>
                        {isReadyForGeneration
                            ? t("certificate-template.ready", {defaultValue: "Ready"})
                            : t("certificate-template.incomplete", {defaultValue: "Incomplete"})}
                    </StatusTag>
                )}
            />

            <Card
                title={t("certificate-template.editor", {defaultValue: "Certificate visual editor"})}
                extra={(
                    <Space wrap>
                        <Button type="primary" icon={<SaveOutlined/>} loading={saving} disabled={!eventBasics} onClick={save}>
                            {t("save", {defaultValue: "Save"})}
                        </Button>
                    </Space>
                )}
            >
                <div className="ati-stack">
                    <div style={{maxWidth: 520}}>
                        <Typography.Text strong>
                            {t("certificate-template-file", {defaultValue: "Certificate template file"})}
                        </Typography.Text>
                        <FileUploadUrlInput
                            value={state.certificateTemplateUrl}
                            onChange={value => setState(previous => ({
                                ...previous,
                                certificateTemplateUrl: value
                            }))}
                            authToken={auth.token}
                            accept=".pdf,application/pdf,image/*"
                            uploadText={fileUploadLabels.upload}
                            clearText={fileUploadLabels.clear}
                            openText={fileUploadLabels.open}
                            emptyText={fileUploadLabels.empty}
                            uploadSuccessText={fileUploadLabels.success}
                            uploadErrorText={fileUploadLabels.error}
                        />
                    </div>

                    <div className="visual-template-editor-layout">
                        <section
                            aria-label={previewAlt}
                            className="visual-template-editor-preview"
                        >
                            <TemplatePlacementEditor
                                authToken={auth.token}
                                backgroundUrl={state.certificateTemplateUrl}
                                backgroundAlt={previewAlt}
                                canDownloadPreview={!!textValue(state.certificateTemplateUrl)}
                                downloadText={t("template-preview-download", {defaultValue: "Download preview"})}
                                downloadingPreview={downloadingPreview}
                                emptyText={t("certificate-template.empty", {defaultValue: "Upload a certificate template to preview placement."})}
                                logicalWidth={CERTIFICATE_CANVAS.width}
                                logicalHeight={CERTIFICATE_CANVAS.height}
                                placements={state.certificateTextFields.map(toTemplatePlacement)}
                                onDownloadPreview={downloadPreview}
                                onPlacementChange={(key, point) => setState(previous => ({
                                    ...previous,
                                    certificateTextFields: previous.certificateTextFields.map(field => field.id === key
                                        ? {...field, x: point.x, y: point.y}
                                        : field)
                                }))}
                            />
                        </section>

                        <section
                            aria-label={fieldListLabels.title}
                            className="visual-template-editor-fields"
                            style={{overflowX: "auto"}}
                        >
                            <div className="visual-template-editor-fields-content">
                                <TemplateTextFieldList
                                    fields={state.certificateTextFields}
                                    labels={fieldListLabels}
                                    logicalWidth={CERTIFICATE_CANVAS.width}
                                    logicalHeight={CERTIFICATE_CANVAS.height}
                                    onAdd={addTextField}
                                    onUpdate={updateTextField}
                                    onRemove={removeTextField}
                                />
                            </div>
                        </section>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default EventCertificateTemplatePage;
